/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import FormValidations from 'cio/utils/form-validations';
import SnippetValidations from 'cio/utils/validations/snippet';
import { liquidTag } from 'cio/helpers/liquid-tag';
import pluralize from 'cio/utils/pluralize';
import paginate from 'cio/utils/paginate';
import { seriesString } from 'cio/utils/formatters';
import { camelize } from '@ember/string';

import RouterService from '@ember/routing/router-service';
import { task } from 'ember-concurrency';
import SnippetModel, { UsageCountType } from 'cio/models/snippet';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import { sortByCaseInsensitive, SortDirection } from 'cio/utils/sort';

import { SortProperty } from 'cio/components/message-library-items-table';
import MessageItem from 'cio/models/message-item';
import ApiService from 'cio/services/api';

const USAGE_COUNT_NAME_MAP: Record<UsageCountType, string> = {
  campaigns: 'campaign',
  broadcasts: 'broadcast',
  transactional: 'transactional message',
};

const USAGE_ITEMS_PER_PAGE = 20;

interface SnippetPageArgs {
  search: string;
  snippet: SnippetModel;
  onSearchChange: (query: string) => unknown;
}

export default class SnippetPage extends Component<SnippetPageArgs> {
  @service router!: RouterService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;

  @tracked isEditingValue = false;
  @tracked hasServerErrors = false;
  @tracked page = 1;
  @tracked sortBy: SortProperty = SortProperty.Name;
  @tracked sortDirection: SortDirection = SortDirection.Ascending;

  readonly itemsPerPage = USAGE_ITEMS_PER_PAGE;

  validations!: FormValidations;

  get snippetLiquid() {
    return `{{${liquidTag(['snippets', this.args.snippet.name])}}}`;
  }

  @cached
  get snippetUsageCountsHumanReadable() {
    let counts = Object.entries(this.args.snippet.usageCounts)
      .filter(([, count]) => count !== 0)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([type, count]) => `${count} ${pluralize(count, USAGE_COUNT_NAME_MAP[type as UsageCountType])}`);

    return seriesString(counts.join());
  }

  get sortedMessageItems() {
    let sortKey = camelize(this.sortBy) as keyof MessageItem;
    let sortedMessageItems = sortByCaseInsensitive(this.args.snippet.messageItems, [sortKey]);

    if (this.sortDirection === SortDirection.Descending) {
      sortedMessageItems = sortedMessageItems.reverse();
    }

    return sortedMessageItems;
  }

  get paginatedMessageItems() {
    return paginate(this.sortedMessageItems, {
      page: this.page - 1,
      perPage: this.itemsPerPage,
    });
  }

  get isSaveDisabled() {
    return (
      !this.auth.hasPermission('journeys/snippets/edit') || this.validations.hasErrors || !this.validations.isTouched
    );
  }

  @action
  cancelEditingValue() {
    this.isEditingValue = false;
    this.args.snippet.rollbackAttributes();
    this.validations.validateModel();
    this.hasServerErrors = false;
  }

  @action
  setValue(value: string) {
    this.args.snippet.value = value;
    this.validations.validateAttribute('value');
    this.hasServerErrors = false;
  }

  @action
  setSort(sortBy: SortProperty, sortDirection: SortDirection) {
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
  }

  @action
  resetSnippet() {
    this.args.snippet.rollbackAttributes();
  }

  saveTask = task(async () => {
    this.hasServerErrors = false;

    this.args.snippet.value = this.args.snippet.value?.trim();

    try {
      await this.validations.validateModel();
    } catch {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return false;
    }

    try {
      let snippet = await this.args.snippet.save();

      this.isEditingValue = false;
      this.alerts.success('snippet.save.success', 'Snippet updated');

      return snippet;
    } catch (reason) {
      this.hasServerErrors = true;

      if (reason.errors?.length && reason.errors[0].source?.pointer) {
        // The back end will handle some validation errors by itself, so we don't need to report those
        // to the apiErrors service. FormValidations and ember data handle the UI for us, conveniently.
        return false;
      }

      this.apiErrors.report(reason);

      return false;
    }
  });

  deleteTask = task(async () => {
    try {
      await this.args.snippet.destroyRecord();
      this.router.replaceWith('snippets.index');
      this.alerts.success('snippet.delete.success', 'Snippet deleted');
    } catch {
      this.alerts.error(
        'snippet.delete.error',
        'Unable to delete snippet. Please try again or contact support for help.',
      );
    }
  });

  fetchUsageTask = task(async (continuation = 0) => {
    try {
      if (this.args.snippet.usageLoaded) {
        return;
      }

      let res = await this.args.snippet.fetchUsage({ continuation });

      if (res.more) {
        this.fetchUsageTask.perform(res.continuation);
      }
    } catch {
      this.alerts.error(
        'snippet.fetch-usage.error',
        'Unable to load all snippet usage. Please try again or contact support for help.',
      );
    }
  });

  constructor(owner: unknown, args: SnippetPageArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: SnippetValidations,
      context: this,
      modelKey: 'args.snippet',
    });

    this.fetchUsageTask.perform();
  }
}
